// English source strings.
// Every piece of user-facing text in the app should live here (or in
// translations.cy.js), referenced from components via useTranslation()'s
// t("dot.path.key") function.

export const en = {
    common: {
        brand: "Proffiliadur",
        errors: {
            serviceUnavailable: "We can't reach the analysis service right now. Please try again in a few minutes.",
            analysisFailed: "Something went wrong while analysing your text. Please check your connection and try again.",
            unexpected: "Something unexpected went wrong. Please reload the page.",
        },
        reload: "Reload page",
        back: "Return"
    },

    header: {
        logoAlt: "Proffiliadur logo",
        nav: {
            home: "Home",
            analyse: "Analyse",
            compare: "Compare",
            features: "Features",
            about: "About",
        },
    },

    landing: {
        bgWords: ["Example", "Words", "Go", "Here", "For", "Testing"],
        description: "Description of the site",
        cta: "Get Started",
    },

    inputBox: {
        title: "Text Input",
        clear: "Clear",
        inputLabel: "Text input {name}",
        placeholder: "Enter your text here...",
        words: "Words: {count}",
        characters: "Characters: {count}",
        tooLong: "Text is too long. Please limit submissions to {max} words or fewer.",
    },

    loading: {
        defaultMessage: "Analysing your text...",
        compareMessage: "Analysing both texts...",
        sub: "This may take a few seconds",
    },

    analysePage: {
        title: "Readability Analysis",
        subtitle: "Profile the readability of your text",
        inspectView: "Inspect View",
        reanalyseCy: "Re-analyse Text in Welsh",
        reanalyseEn: "Re-analyse Text in English",
        analyseCy: "Analyse in Welsh",
        analyseEn: "Analyse in English",
        sampleText: "Sample Texts",
        sampleTextsWelsh: "Welsh Texts",
        sampleTextsEnglish: "English Texts",
        sampleTextsError: "Couldn't load sample texts. Please try again shortly.",
        sampleTextsEmpty: "No sample texts are available.",
        sampleTextsEmptyForLanguage: "No sample texts available for this language.",
    },

    comparePage: {
        title: "Compare Two Texts",
        subtitle: "Analyse and compare readability metrics between two texts",
        button: "Compare Texts",
    },

    textAnnotator: {
        title: "Text analysis",
        editText: "Edit Text",
        legend: { a1: "A1", a2: "A2", b1: "B1", b2plus: "B2+" },
        tooltip: { cefr: "CEFR: {value}" },
        pos: {
            verb: "Verb",
            noun: "Noun",
            adverb: "Adverb",
            adjective: "Adjective",
            particle: "Particle",
            article: "Article",
            preposition: "Preposition",
            pronoun: "Pronoun",
            verbNoun: "Verb noun",
            conjunction: "Conjunction",
            determiner: "Determiner",
            numeral: "Numeral",
        },
        mutation: {
            soft: "Soft mutation",
            nasal: "Nasal mutation",
            aspirate: "Aspirate mutation",
        },
    },

    comparisonTable: {
        text1: "Text 1",
        text2: "Text 2",
        title: "Side-by-side comparison",
        winnerBadge: "{winner} is more readable overall",
        sections: {
            overview: "Overview",
            vocabulary: "Vocabulary",
            grammar: "Grammar",
            syntax: "Syntax",
        },
        rows: {
            readability: "Readability score (percentile)",
            cefrLevel: "CEFR level",
            wordCount: "Word count",
            sentenceCount: "Sentence count",
            avgSentenceLength: "Avg sentence length",
            ttr: "Type Token Ratio",
            a1Coverage: "A1 coverage",
            a2Coverage: "A2 coverage",
            b1Coverage: "B1 coverage",
            stopWords: "Stop words",
            totalMutations: "Total mutations",
            softMutations: "Soft mutations",
            verbNouns: "Verb nouns",
            avgTreeDepth: "Avg tree depth",
            avgHeadDistance: "Avg head distance",
            clausesPerSentence: "Clauses / sentence",
            branchingFactor: "Branching factor",
        },
    },

    resultsBox: {
        analysisResult: "Analysis Result",
        empty: "Submit text to see analysis.",
        outdatedWarning: "The text has been edited since this analysis was generated. Re-analyse the text to update these results.",
        tabs: {
            overview: "Overview",
            proficiency: "Proficiency",
            vocabulary: "Vocabulary",
            grammar: "Grammar",
            structure: "Structure",
            suggestions: "Suggestions",
        },
        cefr: {
            heading: "Language Proficiency Level (CEFR)",
            miniLabel: "CEFR",
            confidence: "{percent}% confidence",
            link: "View official CEFR level descriptors  -",
            infoTip: "The Common European Framework of Reference (CEFR) rates language from A1 (beginner) to C2 (mastery). This is estimated by a machine learning model.",
            descriptions: {
                A1: "Beginner — understands and uses very basic everyday expressions.",
                A2: "Elementary — can communicate in simple, routine tasks.",
                B1: "Intermediate — can deal with most situations likely to arise whilst travelling.",
                B2: "Upper Intermediate — can interact with a degree of fluency and spontaneity.",
            },
        },
        overview: {
            readabilitySection: "Readability",
            textSizeSection: "Text size",
            gulpeaseName: "Gulpease Index (Adapted) - Readability Score",
            gulpeaseLabel: "Higher = more readable",
            avgSentenceLengthName: "Avg Sentence Length",
            avgSentenceLengthLabel: "Words per sentence",
            totalWordsName: "Total Words",
            totalWordsLabel: "Word count",
            sentencesName: "Sentences",
            sentencesLabel: "Total sentences",
        },
        proficiency: {
            coverageSection: "Word coverage by CEFR level",
            coverageInfoTip: "Shows what proportion of words in your text belong to each CEFR vocabulary list.",
            levels: {
                a1: "A1 — Beginner",
                a2: "A2 — Elementary",
                b1: "B1 — Intermediate",
                other: "B2+ or unrecognised",
            },
            basicVocabName: "Basic vocabulary (A1–A2)",
            basicVocabLabel: "of all words",
            intermediateName: "Intermediate (B1)",
            intermediateLabel: "of all words",
        },
        vocabulary: {
            diversityName: "Vocabulary Diversity",
            diversityLabel: "Type-Token Ratio (TTR)",
            note: "The Type-Token Ratio (TTR) measures how varied your vocabulary is. A score closer to 1.0 means most words are unique; a lower score suggests repetition.",
            noteInfoTip: "TTR = unique words ÷ total words. Higher is generally more diverse, though longer texts typically score lower.",
            cefrAccordion: {
                title: "A1 – B1 CEFR Word List Coverage",
                a1Matches: "A1 matches",
                a2Matches: "A2 matches",
                b1Matches: "B1 matches",
                matchesLabel: "Unique words matched",
            },
            frequencyAccordion: {
                title: "Word Frequency Bands (K1–K6)",
                note: "Frequency bands show how common words are in the language. K1 words are the most frequent (e.g. \"the\", \"and\"), K6+ are rare.",
            },
            stopwordsAccordion: {
                title: "Function Words (Stop Words)",
                note: "Function words (e.g. \"y\", \"yn\", \"am\") are grammatical words that carry little meaning on their own. A high proportion is normal in Welsh.",
                functionWordsName: "Function words",
                functionWordsLabel: "Of all tokens",
                uniqueFunctionWordsName: "Unique function words",
                uniqueFunctionWordsLabel: "Distinct forms",
            },
        },
        grammar: {
            posSection: "Parts of speech",
            pos: {
                verbs: "Verbs",
                nouns: "Nouns",
                adverbs: "Adverbs",
                adjectives: "Adjectives",
                conjunctions: "Conjunctions",
                particles: "Particles",
                articles: "Articles",
                prepositions: "Prepositions",
                pronouns: "Pronouns",
            },
            verbFormsSection: "Welsh-specific verb forms",
            verbForms: {
                pluperfect: "Pluperfect",
                imperfect: "Imperfect",
                impersonal: "Impersonal",
                subjunctive: "Subjunctive",
                conditional: "Conditional",
                verbNouns: "Verb nouns",
            },
            mutationsSection: "Mutations",
            mutationsInfoTip: "Welsh mutations are sound changes at the start of words triggered by grammar rules. They are a key feature of Welsh and correlate with certain grammar structures.",
            mutations: {
                total: "Total mutations",
                soft: "Soft mutations",
                nasal: "Nasal mutations",
                aspirate: "Aspirate mutations",
                hProthesis: "H-prothesis",
            },
        },
        structure: {
            complexitySection: "Sentence complexity",
            complexityInfoTip: "These measures describe how complex the grammatical structure of your sentences is. Higher depth and branching generally indicate more complex language.",
            headDistance: "Average head distance",
            headDistanceInfoTip: "Shows how far apart related words tend to be in a sentence. Higher values usually mean sentences are harder to follow because ideas are spread out.",
            avgTreeDepth: "Average tree depth",
            avgTreeDepthInfoTip: "Shows how many layers of information are typically packed into a sentence. Higher values usually mean more complex sentence structures.",
            maxTreeDepth: "Maximum tree depth",
            maxTreeDepthInfoTip: "The complexity of the most complicated sentence in the text. Higher values suggest at least one sentence contains several layers of information.",
            branchingFactor: "Average branching factor",
            branchingFactorInfoTip: "Shows how much information is attached to each part of a sentence. Higher values can indicate denser, more information-rich writing.",
            clauseSection: "Clause structure",
            subjectRatio: "Subject ratio",
            subjectRatioInfoTip: "Shows how often sentences clearly state who or what is performing an action. Higher values generally make text easier to understand.",
            leftRightRatio: "Left-right branching ratio",
            leftRightRatioInfoTip: "Shows whether a language tends to put descriptive or dependent words before or after the main word they attach to (like the verb). The word order matters e.g. Welsh, which normally puts the subject after the verb, would be expected to show more \"after\" patterning than English. A ratio around 1 means it's balanced. Welsh sentences have ratios typically below 1.",
            clausesPerSentence: "Clauses per sentence",
            totalClauses: "Total clauses",
            auxiliaryVerbs: "Auxiliary verbs",
            auxiliaryVerbsInfoTip: "Supporting verbs such as forms of 'bod' or 'gwneud' that help express tense, mood, or aspect. They are a normal part of natural language. They are typically more present in informal speech.",
        },
        suggestions: {
            section: "Suggestions based on your text",
            placeholderNote: "Warning: This is a placeholder reflecting a potential final result.",
            none: "No suggestions .",
            items: {
                repetitiveVocab: {
                    title: "Repetitive vocabulary",
                    body: "Many words are repeated throughout your text. Try using a wider range of vocabulary to make it more engaging.",
                },
                longSentences: {
                    title: "Long sentences",
                    body: "Your sentences are quite long on average. Breaking them into shorter ones can make your text easier to follow.",
                },
                noSoftMutations: {
                    title: "No soft mutations detected",
                    body: "No soft mutations were found. Check that mutation triggers (e.g. after 'yn', 'am') are correctly applied.",
                },
                simpleStructures: {
                    title: "Simple sentence structures",
                    body: "Most sentences follow a simple structure. Adding subordinate clauses can make the text feel more natural.",
                },
                lowB1Coverage: {
                    title: "Low B1 vocabulary coverage",
                    body: "Very few intermediate-level (B1) words were found. If writing for intermediate learners, consider introducing more varied vocabulary.",
                },
            },
        },
    },

    featuresPage: {
        title: "Information about features",
        subtitle: "This page includes information about all the features calculated in analysing the readability.",
        computationsIntro: "This metric contains the following computations:",
        surface: {
            title: "Surface Level Statistics",
            body: "Surface-level statistics provide basic insights based purely on the text itself, without considering the context or meaning of the words. These metrics offer a quick overview of the text's readability. The higher the number for these results suggest a less readable text.",
            computations: [
                "Sentence Count",
                "Token Count - A token consists of words and punctuation",
                "Number Count",
                "Word Count",
                "Average Sentence Length",
                "Unique Words",
                "Type Token Ratio (TTR)",
                "Token Type Ratio (TTR)",
            ],
        },
        vocab: {
            title: "Vocabulary Lists",
            body: "Vocabulary lists break down the text into categorized word groups such as academic terms, common words, or stopwords. These lists help evaluate the text's difficulty level and relevance to particular audiences.",
            computations: [
                "Frequency List - Compares the text with the frequency dataset by CorcenCC",
                "CBAC List - Compares the text with the lists created for CBAC student proficiency levels.",
                "Stopwords - Compares two stopwords list with the text. The first is a list with just Welsh words, the second is a list that consists of Welsh and English words that commonly show up in Welsh text.",
            ],
        },
        pos: {
            title: "Parts of Speech Tagging (PoS)",
            body: "Parts of Speech Tagging involves labelling each word in the text with its grammatical role such as noun, verb, adjective, or adverb, or more. This analysis helps identify sentence structure and understand how different words function in context.",
            listIntro: "The parts of speech included are:",
            items: [
                { term: "Verbs:", description: "actions or state words. e.g. bwyta - to eat, hoffi - to like. Verbs come in different types and are conjugated according to person and number. A verb that isn't conjugated stays in the verb-noun form. e.g. bwyta - to eat, dysgu - to learn." },
                { term: "Nouns:", description: "names of people, places or things. e.g. ci - dog, ysgol - school. In Welsh, nouns can be feminine or masculine, singular or plural, common or proper." },
                { term: "Adjectives:", description: "describe nouns. e.g. mawr - big, da - good." },
                { term: "Adverbs:", description: "describe verbs, the how, when or where something happens. e.g. ddoe - yesterday." },
                { term: "Articles:", description: "definite markers. e.g. y, yr - the." },
                { term: "Prepositions:", description: "show relationships in time or space. e.g. ar - on, gyda - with." },
                { term: "Conjunctions:", description: "connectors of clauses. e.g. a - and, neu - or." },
                { term: "Numerals:", description: "number words. e.g. un - one, dau - two." },
                { term: "Pronouns:", description: "replace nouns. e.g. fi - I, chi - you." },
                { term: "Particles:", description: "verbal/predicative particles like yn." },
                { term: "Determiners:", description: "Some words can replace the article to add more context e.g. pob - every" },
                { term: "Punctuation:", description: "symbols for structure. e.g. comma (,), period (.)" },
            ],
            tensesIntro: "The different tenses are:",
            tenses: [
                "Present: Dw, Rwyt, Mae...",
                "Past: Bues, Prynais, Prynodd...",
                "Imperfect: Roeddwn, Roedd...",
                "Future: Bydda i, Bydd...",
                "Perfect: Dw i wedi...",
                "Conditional: Byddwn i, Byddai...",
                "Subjunctive: bo",
                "Imperative: Cadwch, Darllenwch",
                "Future Subjunctive: Taswn",
                "Relative verbs: sydd, piau",
                "Impersonal: gofynnir, eir",
            ],
            computations: [
                "Coverage of PoS tags: Amount of words tagged.",
                "Number of PoS tag types: Total coverage of different PoS tags.",
                "Lemma Count: Total lemmatized forms.",
                "Number of PoS: Raw counts of each PoS.",
                "Average PoS per sentence: Normalised counts per sentence.",
                "Ratio of type of PoS for all PoS tags: Ratios for each subtype of PoS.",
            ],
        },
        mutations: {
            title: "Mutations",
            body: "Mutations are variations of the initial consonant of the word as triggered by different lexical and morphosyntactic reasons. There are three types of mutation in Welsh: Soft Mutation, Nasal Mutation and Aspirate Mutation. Additionally, h-prothesis is a variation of words beginning with a vowel. Although not considered traditionally a type of mutation, its behaviour is similar.",
            computations: ["Number of Mutations", "Average Mutation type per sentence"],
        },
        syntactic: {
            title: "Syntactic Features",
            body: "Syntax determines the order of words in a sentence. Welsh syntax is characterised by having VSO (verb-subject-object) order. Syntax analysis is done through dependency parse trees via UDPipe.",
            computations: [
                "Tree Depth: the longest path from the root verb to any leaf node.",
                "Tree breadth: the maximum of immediate dependents (children) under any node.",
                "Branching factor: Average number of children per node.",
                "Head distance: Average number of words between each dependent and its head.",
                "Clause density: Number of clause heads per token.",
                "Average leaf ratio: Proportion of terminal leaf nodes to total nodes.",
            ],
        },
    },

    aboutPage: {
        title: "About the Project",
        entries: {
            background: {
                tag: "Background",
                title: "Project Background",
                text: "Proffiliadur was developed as part of an AHRC IAA (Impact Acceleration Account) project involving colleagues from Cardiff University.",
            },
            people: {
                tag: "People",
                title: "The Team",
                roles: {
                    lead: "Project Lead",
                    colead: "Co-lead",
                    ra: "Research Assistant",
                },
            },
            advisory: {
                tag: "Advisory",
                title: "Project Advisory Group",
                text: "The Project Advisory Group included representatives from:",
                list: ["CBAC | WJEC", "National Centre for Learning Welsh", "Welsh Government"],
            },
            affiliation: {
                tag: "Affiliation",
                title: "Affiliation",
                text: "Cardiff University — Computer Science",
            },
        },
    },
};

export default en;
